import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, FileUp, Loader2, RefreshCw } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { OverlayShell } from "@/components/ui/overlay";
import { BrowserFile } from "@/lib/browser-file";
import { errorCode } from "@/lib/error-code";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import type { RedeSalesReportPreview } from "@/types/financeiro";

const ACCEPT = ".xls,.xlsx";
const REPORT_MAX_BYTES = 45_000_000;

const PREVIEW_ERRORS: Record<string, string> = {
	REDE_REPORT_INVALID: "Esta não é a planilha detalhada de vendas da Rede.",
	REDE_REPORT_NO_SALES: "A planilha não contém vendas de cartão aprovadas.",
	REDE_REPORT_MULTIPLE_COMPANIES: "A planilha reúne mais de um estabelecimento.",
	REDE_REPORT_COMPANY_MISMATCH: "A planilha pertence a outro estabelecimento.",
};

export function RedeSalesImportModal({ onClose }: { onClose: () => void }) {
	const [busy, setBusy] = useState(false);
	const close = () => {
		if (!busy) {
			onClose();
		}
	};

	return (
		<OverlayShell
			label="Importar vendas da Rede"
			onClose={close}
			className="items-center justify-center p-4"
		>
			<section
				data-component="rede-sales-import-modal"
				data-busy={busy}
				className="overlay-panel max-w-lg gap-5 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">Importar vendas da Rede</h2>
					<p className="mt-1 text-xs text-muted-foreground">
						Relatório detalhado de vendas em .xlsx
					</p>
				</div>

				<RedeSalesImportPicker onBusyChange={setBusy} onCancel={close} onImported={onClose} />
			</section>
		</OverlayShell>
	);
}

function RedeSalesImportPicker({
	onBusyChange,
	onCancel,
	onImported,
}: {
	onBusyChange: (busy: boolean) => void;
	onCancel: () => void;
	onImported: () => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [data, setData] = useState<string | null>(null);
	const [fileName, setFileName] = useState<string | null>(null);
	const [localError, setLocalError] = useState<string | null>(null);
	const preview = useMutation(
		orpc.integrations.cards.report.preview.mutationOptions({
			onMutate: () => onBusyChange(true),
			onSettled: () => onBusyChange(false),
		}),
	);
	const previewError =
		preview.error === null
			? null
			: (PREVIEW_ERRORS[errorCode(preview.error) ?? ""] ??
				"Não foi possível ler a planilha da Rede.");
	const handleFile = async (file: File) => {
		preview.reset();
		setData(null);
		setFileName(file.name);

		if (!/\.xlsx?$/iu.test(file.name)) {
			setLocalError("O arquivo precisa estar em formato .xls ou .xlsx.");

			return;
		}

		if (file.size > REPORT_MAX_BYTES) {
			setLocalError("A planilha excede o limite de 45 MB.");

			return;
		}

		const encoded = await BrowserFile.toBase64(file).catch(() => null);

		if (encoded === null) {
			setLocalError("Não foi possível ler o arquivo.");

			return;
		}

		setLocalError(null);
		setData(encoded);
		preview.mutate({ data: encoded });
	};

	return (
		<>
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPT}
				data-slot="rede-report-file"
				className="hidden"
				disabled={preview.isPending}
				onChange={(event) => {
					const file = event.currentTarget.files?.[0];

					if (file !== undefined) {
						void handleFile(file);
					}

					event.currentTarget.value = "";
				}}
			/>

			{fileName === null && (
				<button
					type="button"
					data-slot="select-rede-report"
					onClick={() => inputRef.current?.click()}
					className="flex min-h-36 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-input bg-background px-5 text-center transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					<span className="flex size-10 items-center justify-center rounded-lg bg-primary-soft text-primary">
						<FileSpreadsheet className="size-5" />
					</span>
					<span className="text-sm font-semibold text-foreground">Selecionar planilha</span>
				</button>
			)}

			{fileName !== null && (
				<div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3">
					<div className="min-w-0">
						<p className="truncate text-sm font-medium text-foreground">{fileName}</p>
						{preview.isPending && (
							<p className="mt-0.5 text-xs text-muted-foreground">Lendo vendas...</p>
						)}
					</div>
					<Button
						type="button"
						size="sm"
						variant="ghost"
						disabled={preview.isPending}
						data-slot="replace-rede-report"
						onClick={() => inputRef.current?.click()}
					>
						<RefreshCw />
						Trocar
					</Button>
				</div>
			)}

			{preview.isPending && (
				<div className="flex items-center justify-center py-5 text-muted-foreground">
					<Loader2 className="size-5 animate-spin" />
				</div>
			)}

			{(localError !== null || previewError !== null) && (
				<p data-slot="rede-report-error" className="text-sm text-destructive">
					{localError ?? previewError}
				</p>
			)}

			{preview.data !== undefined && data !== null && (
				<RedeSalesImportReview
					data={data}
					preview={preview.data}
					onBusyChange={onBusyChange}
					onCancel={onCancel}
					onImported={onImported}
				/>
			)}

			{preview.data === undefined && !preview.isPending && (
				<div className="flex justify-end">
					<Button type="button" variant="ghost" data-slot="cancel-rede-import" onClick={onCancel}>
						Cancelar
					</Button>
				</div>
			)}
		</>
	);
}

function RedeSalesImportReview({
	data,
	preview,
	onBusyChange,
	onCancel,
	onImported,
}: {
	data: string;
	preview: RedeSalesReportPreview;
	onBusyChange: (busy: boolean) => void;
	onCancel: () => void;
	onImported: () => void;
}) {
	const queryClient = useQueryClient();
	const cancelledSalesLabel =
		preview.cancelled_sales === 1 ? "venda cancelada" : "vendas canceladas";
	const ignoredRowsLabel = preview.ignored_rows === 1 ? "linha ignorada" : "linhas ignoradas";
	const importing = useMutation(
		orpc.integrations.cards.report.import.mutationOptions({
			onMutate: () => onBusyChange(true),
			onSuccess: async (result) => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				let message = "Nenhuma venda nova";

				if (result.created_sales > 0 && result.updated_sales > 0) {
					message = `${result.created_sales} novas e ${result.updated_sales} atualizadas`;
				} else if (result.created_sales > 0) {
					message = `${result.created_sales} ${
						result.created_sales === 1 ? "venda adicionada" : "vendas adicionadas"
					}`;
				} else if (result.updated_sales > 0) {
					message = `${result.updated_sales} ${
						result.updated_sales === 1 ? "venda atualizada" : "vendas atualizadas"
					}`;
				}

				if (result.linked_receipts > 0) {
					message += `. ${result.linked_receipts} ${
						result.linked_receipts === 1 ? "recebimento conciliado" : "recebimentos conciliados"
					}`;
				}

				toast.success(message);
				onImported();
			},
			onError: () => toast.error("Não foi possível importar as vendas da Rede"),
			onSettled: () => onBusyChange(false),
		}),
	);

	return (
		<div data-component="rede-sales-import-review" className="flex flex-col gap-4">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<div className="rounded-lg border border-border p-3">
					<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						Vendas aprovadas
					</p>
					<p className="mt-1 font-mono text-xl font-semibold tabular-nums text-foreground">
						{preview.approved_sales}
					</p>
				</div>
				<div className="rounded-lg border border-border p-3">
					<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						Valor bruto aprovado
					</p>
					<p className="mt-1 font-mono text-xl font-semibold tabular-nums text-foreground">
						{FinanceiroLabels.money(preview.approved_gross_cents)}
					</p>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs text-muted-foreground">Estabelecimento</p>
					<p className="mt-0.5 font-mono text-foreground">{preview.company_number}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">Datas das vendas</p>
					<p className="mt-0.5 text-foreground">
						{format.date(preview.from)} a {format.date(preview.to)}
					</p>
				</div>
			</div>

			{preview.cancelled_sales > 0 && (
				<div className="rounded-lg bg-warning-soft p-3">
					<p className="text-sm font-medium text-foreground">
						{preview.cancelled_sales} {cancelledSalesLabel}
					</p>
					<p className="mt-0.5 text-xs text-muted-foreground">
						Ficam no histórico e não entram na conciliação.
					</p>
				</div>
			)}

			{preview.ignored_rows > 0 && (
				<div className="rounded-lg bg-warning-soft p-3">
					<p className="text-sm font-medium text-foreground">
						{preview.ignored_rows} {ignoredRowsLabel}
					</p>
					<p className="mt-0.5 text-xs text-muted-foreground">
						Vendas negadas, expiradas e pagamentos Pix.
					</p>
				</div>
			)}

			<div className="flex justify-end gap-2">
				<Button type="button" variant="ghost" disabled={importing.isPending} onClick={onCancel}>
					Cancelar
				</Button>
				<Button
					type="button"
					disabled={importing.isPending}
					data-slot="confirm-rede-import"
					onClick={() => importing.mutate({ data })}
				>
					{importing.isPending && <Loader2 className="animate-spin" />}
					{importing.isPending && "Importando vendas..."}
					{!importing.isPending && <FileUp />}
					{!importing.isPending && "Importar relatório"}
				</Button>
			</div>
		</div>
	);
}
